import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import { useToast } from "src/components/ui/toast"
import { fetchCollections, exportUrl } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, formatINR } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import CollectionTrendChart from "src/features/analytics/components/CollectionTrendChart"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

export default function CollectionsReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("quarter")
  const [filters, setFilters] = useState(presetToRange("quarter"))
  const { data, loading, fetch } = useReport(fetchCollections)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id])

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
        exportUrl={exportUrl("collections", filters)}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !data ? (
        <EmptyReportState message="No collection data available." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard
              label="Collected"
              value={formatINR(data.collected)}
              sublabel={data.attainment_pct != null ? `${data.attainment_pct}% of target` : undefined}
            />
            <KpiCard label="Target" value={formatINR(data.target)} />
            <KpiCard label="Outstanding" value={formatINR(data.outstanding)} />
            <KpiCard label="Overdue" value={formatINR(data.overdue)} />
          </div>

          <div className="mb-5">
            <CollectionTrendChart trend={data.trend} />
          </div>

          {data.by_project?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">By Project</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Project</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Collected</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Outstanding</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Overdue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_project.map((row) => (
                    <TableRow key={row.project.id}>
                      <TableCell className="py-2.5 text-slate-900">{row.project.name}</TableCell>
                      <TableCell className="py-2.5 text-right">{formatINR(row.collected)}</TableCell>
                      <TableCell className="py-2.5 text-right">{formatINR(row.outstanding)}</TableCell>
                      <TableCell className="py-2.5 text-right text-red-600">{formatINR(row.overdue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
